import { UnfurlItem } from '@lib/external/unfurl';
import { filterNonDuplicates, getNonDuplicateSelectedCount } from '@lib/utils/chooseMediaUtils';
import { useCallback, useMemo, useState } from 'react';
import { useDuplicateChecker } from './useDuplicateChecker';

export interface UseSelectionManagementProps {
  data: UnfurlItem[];
  existingUniqueIds: Record<string, string>;
  initialSelection?: string[];
}

export interface UseSelectionManagementReturn {
  selectedIds: string[];
  updateSelection: (newIds: string[]) => void;
  clearSelection: () => void;
  addToSelection: (ids: string[]) => void;
  removeFromSelection: (ids: string[]) => void;
  toggleSelection: (id: string) => void;
  isSelected: (id: string) => boolean;
  selectedCount: number;
  nonDuplicateSelectedCount: number;
}

/**
 * Custom hook for managing selection state with duplicate filtering
 * Automatically filters out duplicates from selection operations
 */
export const useSelectionManagement = ({
  data,
  existingUniqueIds,
  initialSelection = [],
}: UseSelectionManagementProps): UseSelectionManagementReturn => {
  const { isDuplicate, isDuplicateById } = useDuplicateChecker({
    data,
    existingUniqueIds,
  });

  const [selectedIds, setSelectedIds] = useState<string[]>(() =>
    filterNonDuplicates(initialSelection, data, isDuplicate),
  );

  // Update selection with automatic duplicate filtering
  const updateSelection = useCallback(
    (newIds: string[]) => {
      setSelectedIds(filterNonDuplicates(newIds, data, isDuplicate));
    },
    [data, isDuplicate],
  );

  // Clear all selections
  const clearSelection = useCallback(() => {
    setSelectedIds([]);
  }, []);

  // Add IDs to selection (filtered)
  const addToSelection = useCallback(
    (ids: string[]) => {
      setSelectedIds((prev) => filterNonDuplicates([...prev, ...ids], data, isDuplicate));
    },
    [data, isDuplicate],
  );

  // Remove IDs from selection
  const removeFromSelection = useCallback((ids: string[]) => {
    setSelectedIds((prev) => prev.filter((id) => !ids.includes(id)));
  }, []);

  // Toggle selection for a single ID (only if not duplicate)
  const toggleSelection = useCallback(
    (id: string) => {
      if (isDuplicateById(id)) return; // Don't allow selection of duplicates

      setSelectedIds((prev) => {
        if (prev.includes(id)) {
          return prev.filter((selectedId) => selectedId !== id);
        } else {
          return [...prev, id];
        }
      });
    },
    [isDuplicateById],
  );

  // Check if an ID is selected
  const isSelected = useCallback(
    (id: string) => {
      return selectedIds.includes(id);
    },
    [selectedIds],
  );

  // Memoized selected count
  const selectedCount = useMemo(() => selectedIds.length, [selectedIds]);

  // Memoized count of non-duplicate selected items
  const nonDuplicateSelectedCount = useMemo(() => {
    return getNonDuplicateSelectedCount(data, selectedIds, existingUniqueIds);
  }, [data, selectedIds, existingUniqueIds]);

  return {
    selectedIds,
    updateSelection,
    clearSelection,
    addToSelection,
    removeFromSelection,
    toggleSelection,
    isSelected,
    selectedCount,
    nonDuplicateSelectedCount,
  };
};
